import { type ElementType, useMemo } from "react";

import { LoadingSpinner } from "@newsela/angelou";

import NewselaGlobalLayout from "@/base";
import InsightsErrorPage from "@/reports/teacher/common/InsightsErrorPage";
import { type User } from "@/types/models/User.interface";
import getUserProperties from "@/users/getUserProperties";
import useUserClassroomGet from "@/users/useUserClassroomGet";
import UnauthenticatedUserError from "@/view/ViewPageError/UnauthenticatedUserError";

interface WithInsightsPrivilegesProps {
  component: ElementType;
}
/**
 * TODO: once root provider is typed you should remove this
 */
interface RootProviderInitialProps {
  RootProvidersInitialProps: {
    userProviderProps: {
      userQueryInitialValue: {
        data: User;
      };
    };
  };
}

/**
 * TODO: once root provider is typed you should remove this
 */
interface RootProviderInitialProps {
  RootProvidersInitialProps: {
    userProviderProps: {
      userQueryInitialValue: {
        data: User;
      };
    };
  };
}

const withInsightsPrivileges = <T,>(props: WithInsightsPrivilegesProps) => {
  const { component: WrappedComponent } = props;
  const WrappedWithApprovedTeachers = (
    ssrProps: RootProviderInitialProps & T,
  ) => {
    const user =
      ssrProps.RootProvidersInitialProps.userProviderProps.userQueryInitialValue
        .data;
    const {
      data: { results: classrooms = [] } = {},
      loading: loadingClasses,
      error: classroomFetchError,
    } = useUserClassroomGet(user?.id, { hide_archived: true });
    const userProperties = useMemo(
      () => getUserProperties(user, { shouldUseNewUserApi: false }),
      [user],
    );
    const {
      isStudent,
      isAuthenticatedUser,
      isLiteUser,
      hasActivePremiumTrial,
    } = userProperties;
    const isVeiled = isLiteUser && !hasActivePremiumTrial;

    if (loadingClasses) {
      return <NewselaGlobalLayout mainContents={<LoadingSpinner />} />;
    }

    if (!isAuthenticatedUser) {
      return (
        <NewselaGlobalLayout mainContents={<UnauthenticatedUserError />} />
      );
    }

    if (isStudent) {
      // TODO: Add student error page
      return <NewselaGlobalLayout mainContents={null} />;
    }

    if (classroomFetchError) {
      return <NewselaGlobalLayout mainContents={<InsightsErrorPage />} />;
    }

    return (
      <WrappedComponent
        classrooms={classrooms}
        loadingClasses={loadingClasses}
        isVeiled={isVeiled}
        {...ssrProps}
      />
    );
  };

  return WrappedWithApprovedTeachers;
};

export default withInsightsPrivileges;
